import { alpha } from '@mui/material/styles';
import { StyleObj } from '../../@types';
import { SKIN } from '../../types/enums';

const BET365_STYLES: StyleObj = {
  accordion: {
    boxShadow: 'none',
    borderRadius: 0,
    '&.Mui-expanded': {
      my: 0,
      '&:before': {
        opacity: 1,
      },
    },
    '&.MuiAccordion-root': {
      borderRadius: 0,
    },
  },
  accordionSummary: {
    background: (theme) => `linear-gradient(150deg, ${theme.palette.primary.main}50 0%, transparent 100%)`,
    borderTop: '1px solid',
    borderColor: 'primary.main',
    minHeight: 0,
    '&.Mui-expanded': {
      minHeight: 0,
    },
    '&:hover': {
      '& .MuiAccordionSummary-content span': {
        color: 'info.main',
      },
    },
    '& .MuiAccordionSummary-content': {
      my: {
        xs: 1.5,
        lg: 2,
      },
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      my: {
        xs: 1.5,
        lg: 2,
      },
    },
    px: {
      xs: 1.5,
      md: 2,
    },
  },
  accordionDetails: {
    p: 0,
    '& .MuiPaper-root': {
      borderRadius: 0,
    },
  },
  expandMoreIcon: {
    color: 'neutral.50',
  },
  marketName: {
    color: 'neutral.50',
    typography: {
      xs: 'h5',
      md: 'h4',
    },
    minWidth: 0,
  },
  marketTitleContainer: {
    flex: 1,
    minWidth: 0,
    gap: 1,
    mr: 1,
  },
  marketLabelContainer: {
    flex: '0 1 auto',
    minWidth: 0,
    gap: 0,
  },
  descriptionButton: {
    p: 0,
    ml: 0.25,
    width: 24,
    height: 24,
    color: 'neutral.50',
    flexShrink: 0,
    fontSize: 14,
    opacity: 0.65,
    transition: 'color 120ms ease, opacity 120ms ease',
    '&:hover': {
      color: 'info.main',
      opacity: 1,
      backgroundColor: 'transparent',
    },
    '&.Mui-focusVisible': {
      color: 'info.main',
      opacity: 1,
      backgroundColor: 'transparent',
    },
  },
  descriptionIcon: {
    fontSize: 'inherit',
  },
  descriptionTooltip: {
    maxWidth: {
      xs: 280,
      md: 360,
    },
    px: 1.25,
    py: 0.75,
    border: '1px solid',
    borderColor: (theme) => alpha(theme.palette.common.white, 0.08),
    borderRadius: 1,
    backgroundColor: 'neutral.900',
    color: 'neutral.50',
    boxShadow: 8,
    typography: 'body3',
    fontWeight: 500,
    lineHeight: 1.35,
    whiteSpace: 'pre-line',
    overflowWrap: 'anywhere',
    '& .MuiTooltip-arrow': {
      color: 'neutral.900',
    },
  },
  betBuilderBadge: {
    height: 18,
    minWidth: 28,
    borderRadius: 0.75,
    px: 0.75,
    backgroundColor: 'info.main',
    color: 'neutral.900',
    typography: 'caption',
    fontWeight: 700,
  },
  checkbox: {
    ml: 1,
    p: 0,
  },
  uncheckedIcon: { color: 'neutral.400' },
};

const GOLA_STYLES: StyleObj = {
  ...BET365_STYLES,
  accordion: {
    ...BET365_STYLES.accordion,
    backgroundColor: 'neutral.600',
  },
  accordionSummary: {
    ...BET365_STYLES.accordionSummary,
    backgroundColor: 'primary.main',
    backgroundImage: 'none',
    borderTop: 'none',
    color: 'neutral.50',
    '& .MuiAccordionSummary-content span': {
      typography: 'body2',
    },
  },
  expandMoreIcon: {
    color: 'neutral.100',
  },
  descriptionButton: {
    ...BET365_STYLES.descriptionButton,
    color: 'neutral.50',
    '&:hover': {
      color: 'neutral.100',
      opacity: 1,
      backgroundColor: 'transparent',
    },
    '&.Mui-focusVisible': {
      color: 'neutral.100',
      opacity: 1,
      backgroundColor: 'transparent',
    },
  },
  betBuilderBadge: {
    ...BET365_STYLES.betBuilderBadge,
    backgroundColor: 'neutral.50',
    color: 'primary.main',
  },
};

const NEOBET_STYLES: StyleObj = {
  ...BET365_STYLES,
  accordion: {
    ...BET365_STYLES.accordion,
    backgroundColor: 'neutral.600',
    borderRadius: '16px !important',
    overflow: 'hidden',
    border: '1px solid',
    borderColor: (theme) => alpha(theme.palette.common.white, 0.06),
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      my: 0,
    },
    '&.MuiAccordion-root': {
      borderRadius: '16px !important',
    },
  },
  accordionSummary: {
    ...BET365_STYLES.accordionSummary,
    background: 'neutral.600',
    borderTop: 'none',
    '& .MuiAccordionSummary-content': {
      my: {
        xs: 1,
        lg: 1.25,
      },
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      my: {
        xs: 1,
        lg: 1.25,
      },
    },
    px: {
      xs: 1.75,
      md: 2,
    },
    py: {
      xs: 0.5,
      md: 0.5,
    },
    '&:hover': {
      '& .MuiAccordionSummary-content span': {
        color: 'text.primary',
      },
    },
  },
  accordionDetails: {
    ...BET365_STYLES.accordionDetails,
    backgroundColor: 'neutral.600',
  },
  expandMoreIcon: {
    color: 'text.secondary',
  },
  marketName: {
    color: 'text.primary',
    typography: {
      xs: 'subtitle1',
      md: 'subtitle1',
    },
    fontWeight: { xs: 700, md: 700 },
    lineHeight: 1.15,
    minWidth: 0,
  },
  marketTitleContainer: {
    ...BET365_STYLES.marketTitleContainer,
    gap: 1,
    mr: 0,
  },
  descriptionButton: {
    ...BET365_STYLES.descriptionButton,
    color: 'text.secondary',
    '&:hover': {
      color: 'text.primary',
      opacity: 1,
      backgroundColor: 'transparent',
    },
    '&.Mui-focusVisible': {
      color: 'text.primary',
      opacity: 1,
      backgroundColor: 'transparent',
    },
  },
  betBuilderBadge: {
    height: 20,
    minWidth: 32,
    borderRadius: 1.5,
    px: 1,
    backgroundColor: 'info.200',
    color: 'neutral.900',
    typography: 'caption',
    fontWeight: 800,
  },
  checkbox: {
    ...BET365_STYLES.checkbox,
    color: 'text.secondary',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    borderRadius: 1.5,
    backgroundColor: 'neutral.800',
    ml: 1,
    mr: 0.5,
    flexShrink: 0,
    '& .MuiCheckbox-root': {
      p: 0,
      m: 0,
    },
    '& svg': {
      fontSize: 14,
      transform: 'rotate(45deg)',
      mr: '1px',
    },
  },
};

export const MARKET_ACCORDION_SKIN_STYLES = {
  [SKIN.BET365]: BET365_STYLES,
  [SKIN.GOLA]: GOLA_STYLES,
  [SKIN.NEOBET]: NEOBET_STYLES,
};
